/**
 * Presentation helpers for Mindspace concepts.
 *
 * Pure functions — mastery derivation, domain colours, importance tiers and
 * relative-time formatting. They lived in `useMindspace.ts` but none of them is
 * a hook, which is why that file was 926 lines.
 */
import type { KnowledgeGraphNode } from '@/lib/api-client';
import { parseUtcDate } from '@/lib/datetime';

/**
 * Derived mastery state - used for the concept badge, the sidebar
 * memory-health row, the future mastery filter, and node coloring fallbacks.
 * Pure function so tests can exercise it without any DB.
 *
 * Built on evidence (backend `mindspace/evidence.py`): a concept the student
 * has not studied is `new` ("Not studied yet"); studied but never asked about
 * is `studied`; once assessed, reviews (FSRS) decide, and lesson-quiz answers
 * decide while there are no reviews yet.
 */
export type MasteryLevel =
  | 'new'
  | 'studied'
  | 'learning'
  | 'mastered'
  | 'struggling';

const STABILITY_MASTERED_DAYS = 30;
const STRUGGLE_THRESHOLD = 0.6;
/** Quiz accuracy below this, with no reviews yet, reads as struggling. */
const QUIZ_STRUGGLE_ACCURACY = 0.5;

export function deriveMastery(input: {
  review_count?: number | null;
  stability?: number | null;
  struggle_score?: number | null;
  quiz_answered?: number | null;
  quiz_correct?: number | null;
  seconds_studied?: number | null;
}): MasteryLevel {
  const reviews = input.review_count ?? 0;
  const stability = input.stability ?? 0;
  const struggle = input.struggle_score ?? 0;
  const answered = input.quiz_answered ?? 0;

  if (reviews === 0) {
    if (answered > 0) {
      const accuracy = (input.quiz_correct ?? 0) / answered;
      return accuracy < QUIZ_STRUGGLE_ACCURACY ? 'struggling' : 'learning';
    }
    return (input.seconds_studied ?? 0) > 0 ? 'studied' : 'new';
  }
  if (struggle > STRUGGLE_THRESHOLD) return 'struggling';
  if (stability >= STABILITY_MASTERED_DAYS) return 'mastered';
  return 'learning';
}
/**
 * Stable domain → color slot mapping. Hashing the domain name keeps the
 * mapping stable across reloads even though node lists change.
 * Concepts with no domain fall back to a neutral gray.
 */
export const DOMAIN_PALETTE = [
  'blue',
  'purple',
  'indigo',
  'cyan',
  'green',
  'violet',
  'amber',
  'orange',
  'rose',
  'teal',
] as const;
export type DomainColor = (typeof DOMAIN_PALETTE)[number] | 'gray';

export function colorSlotForDomain(
  domain: string | null | undefined
): DomainColor {
  if (!domain) return 'gray';
  let hash = 0;
  for (let i = 0; i < domain.length; i++) {
    hash = (hash * 31 + domain.charCodeAt(i)) >>> 0;
  }
  return DOMAIN_PALETTE[hash % DOMAIN_PALETTE.length];
}

/** Tailwind-friendly hex map for the 10-color palette. */
export const DOMAIN_HEX: Record<DomainColor, string> = {
  blue: '#3b82f6',
  purple: '#a855f7',
  indigo: '#6366f1',
  cyan: '#06b6d4',
  green: '#22c55e',
  violet: '#8b5cf6',
  amber: '#f59e0b',
  orange: '#f97316',
  rose: '#f43f5e',
  teal: '#14b8a6',
  gray: '#9ca3af',
};

/**
 * Map composite-ranking importance score (0-1) → react-d3-graph node size.
 * Empirically `[150, 450]` reads nicely at default zoom; below 100 the
 * label disappears under hover state, above 600 nodes overlap aggressively.
 */
export function nodeSizeForImportance(
  importance: number | null | undefined
): number {
  const i = Math.max(0, Math.min(1, importance ?? 0.5));
  return 150 + i * 300;
}

/**
 * Stable user-readable "X days ago" formatter for sidebar timestamps.
 * Returns "Never reviewed" for null inputs. Decoupled from
 * `formatLastUpdated` so callers can compose freely.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const then = parseUtcDate(iso);
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return 'Just now';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return then.toLocaleDateString();
}

/**
 * Future-tense companion to `formatRelativeTime`. Used by the sidebar's
 * "Next review" row.
 *
 *   null  / past timestamp → "Due now"
 *   < 1h                   → "in a few minutes"
 *   < 1d                   → "in N hour(s)"
 *   ≥ 1d                   → "in N day(s)"
 *
 * Pure / locale-independent so tests are deterministic.
 */
export function formatTimeUntil(iso: string | null | undefined): string {
  if (!iso) return 'Not yet scheduled';
  const target = parseUtcDate(iso).getTime();
  const diffMs = target - Date.now();
  if (diffMs <= 0) return 'Due now';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return 'in a few minutes';
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `in ${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30);
  return `in ${months} month${months === 1 ? '' : 's'}`;
}

/**
 * Translate the raw 0-1 importance score into a human-readable tier
 * for the concept title pill. Keeps the bar/number out of the UI in
 * favor of something the user can actually act on.
 */
export type ImportanceTier = 'core' | 'important' | 'supporting' | 'peripheral';

export function importanceTier(
  score: number | null | undefined
): ImportanceTier {
  const s = score ?? 0.5;
  if (s >= 0.75) return 'core';
  if (s >= 0.5) return 'important';
  if (s >= 0.25) return 'supporting';
  return 'peripheral';
}

export const IMPORTANCE_TIER_LABEL: Record<ImportanceTier, string> = {
  core: 'Core concept',
  important: 'Important',
  supporting: 'Supporting',
  peripheral: 'Peripheral',
};

/**
 * Convenience: derive deduplicated, frequency-sorted domain options from
 * the current graph nodes. Used by `DomainTagFilter` and `DomainLegend`
 * so options track the visible subset of the graph.
 */
export function domainOptionsFromNodes(
  nodes: KnowledgeGraphNode[] | undefined
): string[] {
  if (!nodes?.length) return [];
  const counts = new Map<string, number>();
  for (const n of nodes) {
    for (const tag of n.domain_tags ?? []) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([t]) => t);
}

/**
 * Utility function to format relative time
 */
export function formatLastUpdated(lastRunAt: string | null): string {
  if (!lastRunAt) {
    return 'Never updated';
  }

  const now = new Date();
  const lastRun = parseUtcDate(lastRunAt);
  const diffMs = now.getTime() - lastRun.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else {
    return lastRun.toLocaleDateString();
  }
}
